import { api } from './api.js';
import type { UserSession } from '../shared/types.js';

type Listener = () => void;

class SessionStore {
  private user: UserSession | null = null;
  private loading = true;
  private listeners: Set<Listener> = new Set();

  getUser(): UserSession | null {
    return this.user;
  }

  isAuthenticated(): boolean {
    return this.user !== null;
  }

  isLoading(): boolean {
    return this.loading;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    this.listeners.forEach((listener) => listener());
  }

  async init(): Promise<void> {
    this.loading = true;
    this.notify();

    const token = api.getToken();
    if (!token) {
      this.loading = false;
      this.notify();
      return;
    }

    const result = await api.getSession();
    if (result.success && result.data) {
      this.user = result.data;
    } else if (result.rateLimited) {
      console.warn('Rate limited during session init, will retry...');
    } else {
      api.clearToken();
    }

    this.loading = false;
    this.notify();
  }

  setUser(user: UserSession): void {
    this.user = user;
    this.notify();
  }

  async logout(): Promise<void> {
    await api.logout();
    this.user = null;
    this.notify();
  }

  async logoutAllSessions(): Promise<void> {
    await api.logoutAllSessions();
    this.user = null;
    this.notify();
  }
}

export const session = new SessionStore();
